import { Button, Grid, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../app/store/configureStore";
import RandomSampleFormModel from "./common/randomSampleFormModel";
import AppSelectList from "../../app/components/AppSelectList";
import { yupResolver } from "@hookform/resolvers/yup";
import { RandonSampleValidationSchemas } from "./common/randonSampleValidationSchemas";
import AppLabelField from "../../app/components/AppLabelField";
import { useEffect, useState } from "react";
import { getSchemeYearEnrolmentsTotalAsync, submitRandomSampleAsync } from "../../app/slices/pcsSlice";
import { GetSchemeYearEnrolmentsTotalQuery } from "./common/getSchemeYearEnrolmentsTotalQuery";
import { ApiRequestStatus } from "../../app/enums/apiRequestStatus";
import LoadingComponent from "../../app/components/LoadingComponent";
import { MESSAGE_PROCESSING_DETAILS } from "../../app/utils/constant";
import { CreateRandomSampleCommand } from "./common/createRandomSampleCommand";
import { DropdownItem } from "../../app/models/common/dropdownItem";
import RandomSampleSuccessMessage from "./common/RandomSampleSuccessMessage";

export default function RandonSampleForm() {
    const {
        formField: {
            PercentageOfActiveUsers,
            NumberOfUsersInTheSample,
            NumberOfUsersInSchemeYear,
            NumberOfYearsToCheckPreviousValidations
        }
    } = RandomSampleFormModel;

    const dispatch = useAppDispatch();
    const methods = useForm({
        mode: 'all',
        resolver: yupResolver(RandonSampleValidationSchemas)
    });

    const { control, watch, getValues, trigger, reset } = methods;

    const { getSchemeYearEnrolmentsTotalStatus, schemeYearEnrolmentsTotal, submitRandomSampleStatus } = useAppSelector(state => state.pcs);

    const queryParams = new URLSearchParams(window.location.search);
    const schemeYearId = queryParams.get("schemeYearId");

    const [numberOfUsersInTheSchemeYear, setNumberOfUsersInTheSchemeYear] = useState<number>(0);

    useEffect(() => {
        watch((value, { name, type }) => {
            if (name === NumberOfYearsToCheckPreviousValidations.name && type === 'change' && schemeYearId !== null) {
                const data: GetSchemeYearEnrolmentsTotalQuery = {
                    NumberOfYearsToCheck: Number(value.NumberOfYearsToCheckPreviousValidations),
                    SchemeYearId: schemeYearId
                }
                dispatch(getSchemeYearEnrolmentsTotalAsync(data));
            }
        });
    }, [NumberOfYearsToCheckPreviousValidations.name, schemeYearId, watch, dispatch]);

    useEffect(() => {
        watch((_, { name, type }) => {
            if ((name === NumberOfYearsToCheckPreviousValidations.name || name === PercentageOfActiveUsers.name)
                && type === 'change' && schemeYearEnrolmentsTotal !== null) {
                const result = (schemeYearEnrolmentsTotal * Number(getValues(PercentageOfActiveUsers.name))) / 100;
                setNumberOfUsersInTheSchemeYear(result);
            }
        });
    }, [schemeYearEnrolmentsTotal, getValues, watch]);

    const saveRandomSampleAsync = async () => {
        const isStepValid = await trigger();
        if (isStepValid) {
            if (schemeYearId !== null) {
                const data: CreateRandomSampleCommand = {
                    NumberOfYearsToCheck: Number(getValues(NumberOfYearsToCheckPreviousValidations.name)),
                    Percentage: Number(getValues(PercentageOfActiveUsers.name)),
                    SchemeYearId: schemeYearId
                }
                dispatch(submitRandomSampleAsync(data));
            }
        }
    }

    const percentageOfActiveUsersList = () => {
        let dropdownValues: DropdownItem[] = [];
        for (let index = 1; index <= 20; index++) {
            dropdownValues.push({ id: index.toString(), value: index.toString(), code: index.toString() });
        }
        return dropdownValues;
    }

    const clearForm = () => {
        reset({
            [NumberOfYearsToCheckPreviousValidations.name]: '',
            [PercentageOfActiveUsers.name]: ''
        });
    }

    if (getSchemeYearEnrolmentsTotalStatus === ApiRequestStatus.Pending ||
        submitRandomSampleStatus === ApiRequestStatus.Pending) {
        return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />
    }

    if (submitRandomSampleStatus === ApiRequestStatus.Fulfilled) {
        return <RandomSampleSuccessMessage header={'Thank you'}
            body={`Random sample submitted successfully. You can now browse away from this tab.`} />
    }

    return <FormProvider {...methods}>
        <Grid container rowSpacing={4}>

            <Grid item xs={12}></Grid>

            <Grid item xs={12}>
                <Typography component="h1" variant="h4" alignItems="left">
                    Random Sample
                </Typography>
            </Grid>

            <Grid item xs={12}></Grid>

            <Grid item xs={12}>
                <AppSelectList control={control} name={NumberOfYearsToCheckPreviousValidations.name} label={NumberOfYearsToCheckPreviousValidations.label}
                    items={[{ id: '1', value: '1' }, { id: '2', value: '2' }, { id: '3', value: '3' },
                    { id: '4', value: '4' }, { id: '5', value: '5' }, { id: '6', value: '6' }]}
                    disabled={getValues(NumberOfYearsToCheckPreviousValidations.name) !== ''} />
            </Grid>

            {getValues(NumberOfYearsToCheckPreviousValidations.name) !== '' && <>
                <Grid item xs={12}>
                    <AppLabelField label={NumberOfUsersInSchemeYear.label} value={schemeYearEnrolmentsTotal?.toString()} />
                </Grid>

                <Grid item xs={12}>
                    <AppSelectList control={control} name={PercentageOfActiveUsers.name} label={PercentageOfActiveUsers.label}
                        items={percentageOfActiveUsersList()} />
                </Grid>

                {schemeYearEnrolmentsTotal !== null && getValues(PercentageOfActiveUsers.name) !== '' &&
                    <Grid item xs={12}>
                        <AppLabelField label={NumberOfUsersInTheSample.label} value={Math.round(numberOfUsersInTheSchemeYear).toString()} />
                    </Grid>
                }

                <Grid item xs={3}>
                    {schemeYearEnrolmentsTotal !== null && getValues(PercentageOfActiveUsers.name) !== '' &&
                        <Button variant="contained" color="primary" onClick={saveRandomSampleAsync}>
                            Generate
                        </Button>}
                </Grid>

                <Grid item xs={9}>
                    <Button variant="outlined" color="primary" onClick={clearForm}>
                        Clear
                    </Button>
                </Grid>
            </>}
        </Grid>
    </FormProvider>
}
